import itemsData from '../data/items.json';

/**
 * Shop overlay panel (HTML/CSS in #ui-root).
 * Shows merchant inventory, prices, player gold. Arrow keys to navigate, Enter to buy, Escape to close.
 */
export class ShopPanel {
  constructor(uiRoot) {
    this.uiRoot = uiRoot;
    this.isOpen = false;
    this.selectedIndex = 0;
    this.shopItems = []; // array of { itemId, itemData, price, category }
    this.wrapper = null;
    this.slots = [];
    this.goldLabel = null;
    this.infoLabel = null;
    this._create();
  }

  _create() {
    this.wrapper = document.createElement('div');
    this.wrapper.id = 'shop-panel';
    this.wrapper.style.cssText = `
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 320px;
      background: rgba(20, 15, 10, 0.95);
      border: 2px solid rgba(255, 200, 80, 0.4);
      padding: 12px;
      font-family: 'Courier New', monospace;
      color: #cccccc;
      z-index: 200;
      display: none;
      pointer-events: auto;
    `;

    // Title
    const title = document.createElement('div');
    title.style.cssText = `
      font-size: 14px;
      font-weight: bold;
      color: #ffcc44;
      text-align: center;
      margin-bottom: 8px;
    `;
    title.textContent = 'MERCHANT';
    this.wrapper.appendChild(title);

    // Gold display
    this.goldLabel = document.createElement('div');
    this.goldLabel.style.cssText = `
      font-size: 11px;
      color: #ffdd44;
      text-align: right;
      margin-bottom: 6px;
    `;
    this.goldLabel.textContent = 'Gold: 0';
    this.wrapper.appendChild(this.goldLabel);

    // Item list container
    this.listContainer = document.createElement('div');
    this.listContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 3px;
      margin-bottom: 8px;
    `;
    this.wrapper.appendChild(this.listContainer);

    // Info label (stats preview)
    this.infoLabel = document.createElement('div');
    this.infoLabel.style.cssText = `
      font-size: 9px;
      color: #aaaaaa;
      min-height: 30px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      padding-top: 4px;
      margin-top: 4px;
    `;
    this.wrapper.appendChild(this.infoLabel);

    // Controls hint
    const hint = document.createElement('div');
    hint.style.cssText = `
      font-size: 9px;
      color: #666666;
      text-align: center;
      margin-top: 6px;
    `;
    hint.textContent = 'Up/Down: Navigate | Enter: Buy | Esc: Close';
    this.wrapper.appendChild(hint);

    this.uiRoot.appendChild(this.wrapper);
  }

  /**
   * Generate shop inventory based on floor number.
   * @param {number} floor - Current dungeon floor
   * @param {object} rng - SeededRNG instance for deterministic selection
   */
  generateInventory(floor, rng) {
    this.shopItems = [];

    // Build item pool from all categories
    const pool = [];

    // Weapons
    const weaponKeys = Object.keys(itemsData.weapons);
    for (const key of weaponKeys) {
      const data = itemsData.weapons[key];
      const price = 20 + Math.floor(rng.next() * 31); // 20-50g
      pool.push({ itemId: key, itemData: data, price, category: 'weapon' });
    }

    // Armor
    const armorKeys = Object.keys(itemsData.armor);
    for (const key of armorKeys) {
      const data = itemsData.armor[key];
      const price = 30 + Math.floor(rng.next() * 31); // 30-60g
      pool.push({ itemId: key, itemData: data, price, category: 'armor' });
    }

    // Accessories
    const accKeys = Object.keys(itemsData.accessories);
    for (const key of accKeys) {
      const data = itemsData.accessories[key];
      const price = 25 + Math.floor(rng.next() * 21); // 25-45g
      pool.push({ itemId: key, itemData: data, price, category: 'accessory' });
    }

    // Health potion (always in shop)
    this.shopItems.push({
      itemId: 'health_potion',
      itemData: itemsData.consumables.health_potion,
      price: 10,
      category: 'consumable',
    });

    // Shuffle pool and pick 5 more items (total 6 with potion)
    for (let i = pool.length - 1; i > 0; i--) {
      const j = rng.nextInt(0, i);
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    // Favor items appropriate for floor level
    const itemCount = Math.min(5, pool.length);
    for (let i = 0; i < itemCount; i++) {
      this.shopItems.push(pool[i]);
    }
  }

  /**
   * Open the shop panel.
   * @param {number} playerGold - Current player gold
   */
  open(playerGold) {
    this.isOpen = true;
    this.selectedIndex = 0;
    this.wrapper.style.display = 'block';
    this._renderItems(playerGold);
    this._updateInfo();
  }

  /**
   * Close the shop panel.
   */
  close() {
    this.isOpen = false;
    this.wrapper.style.display = 'none';
  }

  /**
   * Render the item list slots.
   * @param {number} playerGold - Current player gold
   */
  _renderItems(playerGold) {
    this.listContainer.innerHTML = '';
    this.slots = [];
    this.goldLabel.textContent = 'Gold: ' + playerGold;

    for (let i = 0; i < this.shopItems.length; i++) {
      const item = this.shopItems[i];
      const slot = document.createElement('div');
      const canAfford = playerGold >= item.price;
      const selected = i === this.selectedIndex;

      slot.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 3px 6px;
        font-size: 10px;
        background: ${selected ? 'rgba(255, 200, 80, 0.15)' : 'rgba(50, 50, 50, 0.4)'};
        border: 1px solid ${selected ? 'rgba(255, 200, 80, 0.4)' : 'rgba(255, 255, 255, 0.08)'};
        color: ${canAfford ? '#cccccc' : '#666666'};
        cursor: default;
      `;

      const nameSpan = document.createElement('span');
      nameSpan.textContent = item.itemData.name;

      const priceSpan = document.createElement('span');
      priceSpan.style.color = canAfford ? '#ffdd44' : '#884444';
      priceSpan.textContent = item.price + 'g';

      slot.appendChild(nameSpan);
      slot.appendChild(priceSpan);
      this.listContainer.appendChild(slot);
      this.slots.push(slot);
    }
  }

  /**
   * Update the info label with stats of the currently selected item.
   */
  _updateInfo() {
    if (this.selectedIndex < 0 || this.selectedIndex >= this.shopItems.length) {
      this.infoLabel.textContent = '';
      return;
    }

    const item = this.shopItems[this.selectedIndex];
    const data = item.itemData;
    const lines = [data.name + ' (' + item.category + ')'];

    if (data.damage) lines.push('Damage: ' + data.damage[0] + '-' + data.damage[1]);
    if (data.speed !== undefined && data.type === 'weapon') lines.push('Speed: ' + data.speed + 's');
    if (data.range) lines.push('Range: ' + data.range + 'px');
    if (data.defense) lines.push('Defense: +' + Math.round(data.defense * 100) + '%');
    if (data.speedMod) lines.push('Speed: ' + (data.speedMod > 0 ? '+' : '') + data.speedMod);
    if (data.hpBonus) lines.push('HP: +' + data.hpBonus);
    if (data.damageMod) lines.push('Damage: +' + Math.round(data.damageMod * 100) + '%');
    if (data.critChance) lines.push('Crit: +' + Math.round(data.critChance * 100) + '%');
    if (data.healAmount) lines.push('Heals: ' + data.healAmount + ' HP');

    this.infoLabel.textContent = lines.join(' | ');
  }

  /**
   * Handle input while shop is open.
   * @param {object} input - InputManager instance
   * @param {number} playerGold - Current player gold
   * @returns {object|null} Purchase result: { itemId, itemData, price } or null
   */
  handleInput(input, playerGold) {
    if (!this.isOpen) return null;

    if (input.isKeyJustPressed('Escape')) {
      this.close();
      return null;
    }

    let changed = false;

    if (input.isKeyJustPressed('ArrowUp') || input.isKeyJustPressed('KeyW')) {
      this.selectedIndex = Math.max(0, this.selectedIndex - 1);
      changed = true;
    }

    if (input.isKeyJustPressed('ArrowDown') || input.isKeyJustPressed('KeyS')) {
      this.selectedIndex = Math.min(this.shopItems.length - 1, this.selectedIndex + 1);
      changed = true;
    }

    if (changed) {
      this._renderItems(playerGold);
      this._updateInfo();
    }

    if (input.isKeyJustPressed('Enter')) {
      const item = this.shopItems[this.selectedIndex];
      if (item && playerGold >= item.price) {
        // Remove purchased item from shop
        const purchased = this.shopItems.splice(this.selectedIndex, 1)[0];
        if (this.selectedIndex >= this.shopItems.length) {
          this.selectedIndex = Math.max(0, this.shopItems.length - 1);
        }
        this._renderItems(playerGold - purchased.price);
        this._updateInfo();

        // Close shop if empty
        if (this.shopItems.length === 0) {
          this.close();
        }

        return purchased;
      }
    }

    return null;
  }

  destroy() {
    if (this.wrapper && this.wrapper.parentNode) {
      this.wrapper.parentNode.removeChild(this.wrapper);
    }
    this.wrapper = null;
    this.slots = [];
    this.shopItems = [];
  }
}
